import { UzBadge, UzSpinner } from '@utazzo/ui';
import {
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  UserCog,
} from 'lucide-react';
import React, { useState } from 'react';

import { type PricingLogRow } from '../itineraries.api';
import { useItineraryPricingLogs } from '../use-itineraries';

/**
 * Admin-only Logs tab.
 *
 * Renders every post-publish pricing-change event on this itinerary,
 * newest first. Each row shows: source (customer reprice / republish /
 * admin edit), actor identity, status (Pending vs Confirmed with the
 * confirmedBy + confirmedAt stamp), total-money delta grid (netCost,
 * netMarkup, GST, TCS, grand), and an expandable per-component
 * breakdown for the two customer-reprice sources.
 *
 * Pending rows sit at the top with a distinctive amber badge - these
 * are "customer saw the price bump, hasn't clicked Confirm yet" cases,
 * which is exactly the kind of drop-off signal we want visible.
 */
export const LogsTab = ({ itineraryId }: { itineraryId: string }): React.JSX.Element => {
  const query = useItineraryPricingLogs(itineraryId);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-zinc-500">
        <UzSpinner />
        <span className="ml-2 text-sm">Loading pricing logs…</span>
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
        <div className="font-semibold">Failed to load pricing logs</div>
        <div className="mt-1">
          {query.error instanceof Error ? query.error.message : 'Please try again in a moment.'}
        </div>
      </div>
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500">
        No pricing changes yet on this itinerary. Reprice events on the customer share page and
        admin money edits show up here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600">
        Every reprice event on this itinerary — customer confirmations on the share page,
        republishes, admin money edits. Rows showing{' '}
        <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">
          Pending
        </span>{' '}
        are cases where a customer opened the reprice popup but hasn&rsquo;t clicked Confirm yet.
      </div>
      {rows.map((row) => (
        <LogRow key={row.id} row={row} />
      ))}
    </div>
  );
};

// ============================================================================
// One log row
// ============================================================================

const SOURCE_META: Record<
  PricingLogRow['changeSource'],
  { label: string; badge: 'default' | 'success' | 'warning' | 'secondary' }
> = {
  SHARE_PAY_NOW_REPRICE: { label: 'Full-Payment Reprice', badge: 'default' },
  SHARE_PAY_PLAN_I1_REPRICE: { label: 'Plan I1 Reprice', badge: 'default' },
  PREVIEW_PUBLISH: { label: 'Republish', badge: 'success' },
  ADMIN_MANUAL_EDIT: { label: 'Admin Money Edit', badge: 'secondary' },
};

const LogRow = ({ row }: { row: PricingLogRow }): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const source = SOURCE_META[row.changeSource];
  const isPending = row.confirmedAt === null || row.confirmedAt === undefined;
  const isCustomer = row.actorKind === 'CUSTOMER';

  const grandDelta = BigInt(row.newGrandTotalPaise) - BigInt(row.previousGrandTotalPaise);
  const grandUp = grandDelta > 0n;

  return (
    <div
      className={
        'rounded-lg border bg-white shadow-sm ' +
        (isPending ? 'border-amber-300' : 'border-zinc-200')
      }
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5">
            {open ? (
              <ChevronDown className="h-4 w-4 text-zinc-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-zinc-500" />
            )}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <UzBadge tone={source.badge}>{source.label}</UzBadge>
              {isPending ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  <Clock className="h-3 w-3" />
                  Pending acceptance
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  <Check className="h-3 w-3" />
                  Confirmed
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-600">
              <span className="inline-flex items-center gap-1">
                {isCustomer ? (
                  <User className="h-3.5 w-3.5" />
                ) : (
                  <UserCog className="h-3.5 w-3.5" />
                )}
                {row.actorLabel || (isCustomer ? 'Customer' : row.actorEmail || 'Agent')}
              </span>
              <span className="text-zinc-400">·</span>
              <span title={row.createdAt}>{fmtDateTime(row.createdAt)}</span>
              {row.confirmedAt && row.confirmedBy && (
                <>
                  <span className="text-zinc-400">·</span>
                  <span className="text-zinc-500">
                    Confirmed by <span className="font-semibold">{row.confirmedBy}</span> on{' '}
                    {fmtDateTime(row.confirmedAt)}
                  </span>
                </>
              )}
            </div>
            {row.notes && <p className="mt-1 text-xs text-zinc-500 italic">{row.notes}</p>}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div
            className={
              'inline-flex items-center gap-1 text-lg font-extrabold tabular-nums ' +
              (grandDelta === 0n ? 'text-zinc-500' : grandUp ? 'text-red-600' : 'text-emerald-600')
            }
          >
            {grandDelta === 0n ? null : grandUp ? (
              <ArrowUpRight className="h-4 w-4" />
            ) : (
              <ArrowDownRight className="h-4 w-4" />
            )}
            {fmtDelta(grandDelta)}
          </div>
          <div className="mt-0.5 text-xs text-zinc-500">
            Grand: {fmtPaise(row.previousGrandTotalPaise)} → {fmtPaise(row.newGrandTotalPaise)}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-3">
          <MoneyDeltaGrid row={row} />
          {row.componentChanges.length > 0 && <ComponentDeltas rows={row.componentChanges} />}
          {row.shareToken && (
            <p className="mt-3 text-[11px] text-zinc-500">
              Share token: <code>{row.shareToken}</code>
            </p>
          )}
          {row.actorKind === 'AGENT' && row.actorEmail && (
            <p className="mt-1 text-[11px] text-zinc-500">
              Agent: <code>{row.actorEmail}</code>{' '}
              {row.actorUserId && (
                <span className="text-zinc-400">(user id {row.actorUserId})</span>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Money delta grid
// ============================================================================

const MoneyDeltaGrid = ({ row }: { row: PricingLogRow }): React.JSX.Element => {
  const items: Array<{ label: string; prev: string; next: string }> = [
    { label: 'Net cost', prev: row.previousNetCostPaise, next: row.newNetCostPaise },
    { label: 'Net markup', prev: row.previousNetMarkupPaise, next: row.newNetMarkupPaise },
    { label: 'GST', prev: row.previousGstPaise, next: row.newGstPaise },
    { label: 'TCS', prev: row.previousTcsPaise, next: row.newTcsPaise },
    { label: 'Grand total', prev: row.previousGrandTotalPaise, next: row.newGrandTotalPaise },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
      {items.map((it) => {
        const delta = BigInt(it.next) - BigInt(it.prev);
        const up = delta > 0n;
        return (
          <div
            key={it.label}
            className={
              'rounded-md border bg-white px-3 py-2 text-xs ' +
              (delta === 0n ? 'border-zinc-200' : up ? 'border-red-200' : 'border-emerald-200')
            }
          >
            <div className="text-[10px] font-semibold tracking-wide text-zinc-500 uppercase">
              {it.label}
            </div>
            <div className="mt-0.5 flex items-baseline justify-between">
              <span className="text-zinc-700 tabular-nums">
                {fmtPaise(it.prev)} → {fmtPaise(it.next)}
              </span>
              <span
                className={
                  'ml-2 font-semibold tabular-nums ' +
                  (delta === 0n ? 'text-zinc-500' : up ? 'text-red-600' : 'text-emerald-600')
                }
              >
                {fmtDelta(delta)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Per-component deltas (only present on customer reprice events)
// ============================================================================

const ComponentDeltas = ({
  rows,
}: {
  rows: PricingLogRow['componentChanges'];
}): React.JSX.Element => (
  <div className="mt-3">
    <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-zinc-700">
      <AlertCircle className="h-3.5 w-3.5" />
      Components that moved
    </div>
    <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
      {rows.map((c) => {
        const delta = BigInt(c.deltaPaise);
        const up = delta > 0n;
        return (
          <li key={c.id} className="flex items-center justify-between gap-4 px-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="mr-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 uppercase">
                {c.componentKind}
              </span>
              <span className="text-zinc-700">{c.componentTitle || <em>(untitled)</em>}</span>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-xs text-zinc-500 tabular-nums">
                {fmtPaise(c.previousCostPaise)} → {fmtPaise(c.newCostPaise)}
              </div>
              <div
                className={
                  'text-sm font-bold tabular-nums ' +
                  (delta === 0n ? 'text-zinc-500' : up ? 'text-red-600' : 'text-emerald-600')
                }
              >
                {fmtDelta(delta)}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

// ============================================================================
// Helpers
// ============================================================================

const fmtPaise = (paise: string): string => {
  const rupees = Number(BigInt(paise)) / 100;
  if (!Number.isFinite(rupees)) return '--';
  return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDelta = (delta: bigint): string => {
  if (delta === 0n) return '₹0.00';
  const abs = delta < 0n ? -delta : delta;
  const rupees = Number(abs) / 100;
  const sign = delta > 0n ? '+' : '-';
  return `${sign}₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
